// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import selectAltWarehouse from "@guestsupply/App/Handlers/BackOrders/SelectAltWarehouse";
import setShipFromAltDCModalIsOpen from "@guestsupply/App/Handlers/BackOrders/SetShipFromAltDCModalIsOpen";
import { xCommonLoadingOverlay } from "@guestsupply/App/Handlers/XNG/Common";
import { BackOrderModel } from "@guestsupply/App/Services/BackorderManagementService";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    extendedStyles?: ShipFromAltDCModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: CustomAppState) => ({
    modalIsOpen: state.xBackorder.isOpen,
    backorder: state.xBackorder.backorder,
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    setShipFromAltDCModalIsOpen,
    selectAltWarehouse,
    xCommonLoadingOverlay,
    updateCartLine,
    loadCurrentCart,
};

export interface ShipFromAltDCModalStyles {
    shipFromAltDCModal?: ModalPresentationProps;
    confirmShipFromAltDCModal?: ModalPresentationProps;
    confirmShipFromAltDCTitle?: TypographyProps;
    innerWrapper?: InjectableCss;
    descriptionText?: TypographyProps;
    availableAltWarehouseWrapper?: InjectableCss;
    shipFromCurrentWrapper?: InjectableCss;
    selectButton?: ButtonPresentationProps;
    shipFromCurrentButton?: ButtonPresentationProps;
}

// Xng-Changes: please check ShipFromAltDCModalExtensions.ts for styles
export const shipFromAltDCModalStyles: ShipFromAltDCModalStyles = {};

const ShipFromAltDCModal = ({
    backorder,
    modalIsOpen,
    cartPageUrl,
    history,
    setShipFromAltDCModalIsOpen,
    selectAltWarehouse,
    xCommonLoadingOverlay,
    updateCartLine,
    loadCurrentCart,
    extendedStyles,
}: Props) => {
    const isCartLine = !!(backorder as any)?.productId;
    const [styles] = useState(() => mergeToNew(shipFromAltDCModalStyles, extendedStyles));
    const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);

    const toaster = useContext(ToasterContext);

    if (!backorder) {
        return null;
    }

    const modalCloseHandler = () => {
        setShipFromAltDCModalIsOpen({ modalIsOpen: false });
        // XNG-CHANGES GSS-987 : adding timeout, because if the modals close at the same time, the scrollbar disappears
        setTimeout(() => {
            setConfirmModalIsOpen(false);
        }, 0);
    };

    const handleSelect = () => {
        if (isCartLine) {
            xCommonLoadingOverlay({ key: "add", value: "ship_from_alt_DC_modal" });
            updateCartLine({
                cartLine: {
                    ...(backorder as CartLineModel),
                    properties: {
                        selectAltWarehouse: "true",
                    },
                },
                onComplete() {
                    modalCloseHandler();
                    xCommonLoadingOverlay({ key: "clear", value: "ship_from_alt_DC_modal" });
                },
            });
        } else {
            setConfirmModalIsOpen(true);
        }
    };

    const moveToCartHandler = () => {
        xCommonLoadingOverlay({ key: "add", value: "ship_from_alt_DC_modal" });
        selectAltWarehouse({
            backorder: backorder as BackOrderModel,
            onSuccess: () => {
                xCommonLoadingOverlay({ key: "clear", value: "ship_from_alt_DC_modal" });
                history.push(cartPageUrl as string);
                setShipFromAltDCModalIsOpen({ modalIsOpen: false });
                loadCurrentCart();
            },
            onError: () => {
                xCommonLoadingOverlay({ key: "clear", value: "ship_from_alt_DC_modal" });
                setShipFromAltDCModalIsOpen({ modalIsOpen: false });
                toaster.addToast({
                    body: translate("There was an error processing your request."),
                    messageType: "danger",
                });
            },
        });
    };

    return (
        <>
            <Modal
                {...styles.shipFromAltDCModal}
                headline={siteMessage("GUEST_BackOrder_Alternate_DC_Modal_Title")}
                isOpen={!confirmModalIsOpen && modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledWrapper {...styles.innerWrapper}>
                    <Typography {...styles.descriptionText}>
                        {siteMessage("GUEST_BackOrder_Alternate_DC_Modal_Text")}
                    </Typography>
                    <StyledWrapper {...styles.availableAltWarehouseWrapper}>
                        {(backorder as BackOrderModel)?.availableAltWarehouse ||
                            backorder.properties.availableAltWarehouse}
                        <Button {...styles.selectButton} onClick={handleSelect} aria-label="available AltWarehouse">
                            {translate("Select")}
                        </Button>
                    </StyledWrapper>
                    <StyledWrapper {...styles.shipFromCurrentWrapper}>
                        <StyledWrapper>
                            <Button
                                {...styles.shipFromCurrentButton}
                                onClick={modalCloseHandler}
                                aria-label="ship From Current"
                            >
                                {siteMessage("GUEST_BackOrder_Current_DC_Modal_Button")}
                            </Button>
                        </StyledWrapper>
                        {siteMessage("GUEST_BackOrder_Current_DC_Modal_Message")}
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
            <Modal {...styles.confirmShipFromAltDCModal} isOpen={confirmModalIsOpen} handleClose={modalCloseHandler}>
                <Typography {...styles.confirmShipFromAltDCTitle}>
                    {siteMessage("GUEST_BackOrder_Alternate_DC_Confirm_Modal_text")}
                </Typography>
                <Button onClick={moveToCartHandler} aria-label="Move To Cart">
                    {translate("Move To Cart")}
                </Button>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ShipFromAltDCModal));
